<template>
  <div class="card">
    <div class="card-header">
      <h4 class="card-title">Mapa de cobertura</h4>
      <p class="card-category">
        Recorrido del período · una unidad por vez · el fondo se cambia con el control de capas
        (arriba a la izquierda)
      </p>
    </div>
    <div class="card-body">
      <div class="cg-toolbar">
        <span class="cg-lbl">Unidad:</span>
        <button
          v-for="u in unidades"
          :key="u.uid"
          type="button"
          class="cg-chip"
          :class="{ activo: u.uid === uidActivo }"
          @click="seleccionar(u.uid)"
        >
          <i class="cg-sw" :style="{ background: u.hex }"></i>
          <span>{{ u.alias }}</span>
        </button>

        <div class="cg-modos">
          <span class="cg-lbl">Colorear por:</span>
          <button
            data-ayuda="Lo calcula el servidor: compara cuándo se tomó la posición con cuándo llegó. Azul, llegó en vivo. Rojo, tardó más de 4 minutos: el equipo no tenía señal ahí y la guardó en memoria hasta reconectarse."
            type="button"
            class="cg-btn"
            :class="{ activo: modo === 'buffer' }"
            @click="setModo('buffer')"
          >
            Señal / sin señal
          </button>
          <button
            data-ayuda="Es la señal que mide el propio equipo y manda en cada posición, de 0 a 5: no la calcula el servidor. Rojo es señal débil, verde es buena. Gris cuando el equipo no la informó."
            type="button"
            class="cg-btn"
            :class="{ activo: modo === 'gsm' }"
            @click="setModo('gsm')"
          >
            Señal GSM
          </button>
          <span class="cg-sep"></span>
          <button
            data-ayuda="Deja solo las posiciones tomadas con el equipo alimentado por el vehículo (más de 5 V: sirve igual para 12 V y para 24 V). Útil en unidades con llave de corte: al bajarla el equipo queda con su batería interna y, cuando se agota, deja de reportar. Eso parece falta de señal y es falta de energía."
            type="button"
            class="cg-btn"
            :class="{ activo: soloConEnergia }"
            @click="toggleEnergia"
          >
            Solo con alimentación
          </button>
          <button
            data-ayuda="Vuelve a encuadrar el mapa sobre las posiciones que se están mostrando."
            type="button"
            class="cg-btn"
            @click="centrar"
          >
            Centrar
          </button>
        </div>
      </div>

      <div ref="mapa" class="cg-mapa"></div>

      <div class="cg-timebar">
        <div class="cg-tb-top">
          <span class="cg-lbl">Día:</span>
          <select v-model="diaElegido" class="form-control cg-select" @change="irADia">
            <option value="">Todo el período</option>
            <option v-for="d in dias" :key="d" :value="d">{{ d.slice(8, 10) }}/{{ d.slice(5, 7) }}</option>
          </select>
          <button type="button" class="btn btn-default btn-xs" @click="verTodo">
            Ver todo el período
          </button>
          <span class="cg-tb-rango">{{ etiquetaRango }}</span>
        </div>
        <div class="cg-slider">
          <div class="cg-track"><div class="cg-fill" :style="estiloFill"></div></div>
          <input v-model.number="lo" type="range" min="0" :max="totalMin" aria-label="Desde" @input="onSlider" />
          <input v-model.number="hi" type="range" min="0" :max="totalMin" aria-label="Hasta" @input="onSlider" />
        </div>
      </div>

      <div class="cg-leyenda-mapa">
        <template v-if="modo === 'gsm'">
          <span>Señal GSM</span>
          <span class="cg-lg">débil</span>
          <span class="cg-barra-gsm"></span>
          <span class="cg-lg">fuerte (0–{{ gsmMax }})</span>
          <span class="cg-lg"><i style="background: #9aa0ae"></i>sin dato</span>
        </template>
        <template v-else>
          <span class="cg-lg"><i style="background: #6fa3e0"></i>Con señal (en vivo)</span>
          <span class="cg-lg"><i style="background: #e93241"></i>Sin señal (bufferado)</span>
        </template>
        <span class="cg-lg">◕ dona = parada (duración al centro, aro por % sin señal)</span>
        <span class="cg-nota-mapa">{{ nota }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { DAY, fmtDwell, gsmColor, nf, parseDT, pct } from "./cobertura";

export default {
  name: "MapaCobertura",
  props: { unidades: { type: Array, required: true } },
  data() {
    return {
      mapa: null,
      capaPuntos: null,
      renderer: null,
      uidActivo: null,
      modo: "buffer",
      soloConEnergia: false,
      encuadrado: false,
      limites: null,
      lo: 0,
      hi: 0,
      diaElegido: "",
      nota: "",
    };
  },
  computed: {
    unidad() {
      return this.unidades.find((u) => u.uid === this.uidActivo) || this.unidades[0];
    },
    totalMin() {
      if (!this.unidad) return 1;
      return Math.max(1, Math.round((this.unidad.t1 - this.unidad.t0) / 60000));
    },
    dias() {
      if (!this.unidad) return [];
      const set = new Set();
      this.unidad.puntos.forEach((p) => set.add(p.t.slice(0, 10)));
      return [...set].sort();
    },
    gsmMax() {
      return (this.unidad && this.unidad.gsmMax) || 5;
    },
    estiloFill() {
      const tot = this.totalMin || 1;
      const a = Math.min(this.lo, this.hi);
      const b = Math.max(this.lo, this.hi);
      return { left: (a / tot) * 100 + "%", width: ((b - a) / tot) * 100 + "%" };
    },
    etiquetaRango() {
      if (this.lo <= 0 && this.hi >= this.totalMin) return "todo el período";
      return `${this.fmtTB(this.rangoLo)}  →  ${this.fmtTB(this.rangoHi)}`;
    },
    rangoLo() {
      return this.unidad ? this.unidad.t0 + Math.min(this.lo, this.hi) * 60000 : 0;
    },
    rangoHi() {
      return this.unidad ? this.unidad.t0 + Math.max(this.lo, this.hi) * 60000 : 0;
    },
  },
  watch: {
    unidades: {
      handler() {
        if (!this.unidades.some((u) => u.uid === this.uidActivo)) {
          this.uidActivo = this.unidades.length ? this.unidades[0].uid : null;
          this.encuadrado = false;
        }
        this.$nextTick(() => {
          this.resetRango();
          this.dibujar();
        });
      },
      immediate: true,
    },
  },
  mounted() {
    this.crearMapa();
    this.resetRango();
    this.dibujar();
  },
  beforeDestroy() {
    if (this.mapa) {
      this.mapa.remove();
      this.mapa = null;
    }
  },
  methods: {
    crearMapa() {
      if (this.mapa || !this.$refs.mapa) return;
      this.mapa = L.map(this.$refs.mapa, { preferCanvas: true, zoomControl: true }).setView(
        [-26.83, -65.2],
        11,
      );
      // Los basemaps de CARTO (los que usaba el tablero suelto) hoy devuelven el
      // tile con la marca "API KEY REQUIRED", así que quedan afuera.
      const bases = {
        Calles: L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          subdomains: "abc",
          maxZoom: 19,
          attribution: "© OpenStreetMap",
        }),
        Satélite: L.tileLayer(
          "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
          { maxZoom: 20, attribution: "Imagery © Esri, Maxar, Earthstar Geographics" },
        ),
      };
      bases["Calles"].addTo(this.mapa);
      L.control.layers(bases, null, { position: "topleft", collapsed: true }).addTo(this.mapa);
      this.renderer = L.canvas({ padding: 0.5 });
      this.capaPuntos = L.layerGroup().addTo(this.mapa);
    },

    resetRango() {
      this.lo = 0;
      this.hi = this.totalMin;
      this.diaElegido = "";
    },

    seleccionar(uid) {
      this.uidActivo = uid;
      this.encuadrado = false;
      this.resetRango();
      this.dibujar();
    },
    setModo(m) {
      this.modo = m;
      this.dibujar();
    },
    toggleEnergia() {
      this.soloConEnergia = !this.soloConEnergia;
      this.dibujar();
    },
    centrar() {
      if (this.limites && this.mapa) this.mapa.fitBounds(this.limites, { padding: [24, 24] });
    },
    onSlider() {
      this.diaElegido = "";
      this.dibujar();
    },
    verTodo() {
      this.resetRango();
      this.encuadrado = false;
      this.dibujar();
    },
    irADia() {
      if (!this.diaElegido) {
        this.verTodo();
        return;
      }
      const ds = parseDT(this.diaElegido + " 00:00").getTime();
      const de = ds + DAY;
      this.lo = this.acotar(Math.round((ds - this.unidad.t0) / 60000));
      this.hi = this.acotar(Math.round((de - this.unidad.t0) / 60000));
      this.encuadrado = false;
      this.dibujar();
    },
    acotar(v) {
      return Math.max(0, Math.min(this.totalMin, v));
    },

    /**
     * Lo llama la tabla de cortes: enfoca el tramo de un corte puntual.
     *
     * Se agrega un margen a cada lado porque durante el corte, por definición,
     * no hay tramas: sin margen el mapa queda vacío. Con él se ven las últimas
     * posiciones antes de perder señal y las primeras del reenganche.
     */
    verTramo(uid, t0, t1) {
      const u = this.unidades.find((x) => x.uid === uid);
      if (!u) return;
      this.uidActivo = uid;
      this.diaElegido = "";
      const margen = 15 * 60000;
      this.$nextTick(() => {
        this.lo = this.acotar(Math.round((t0 - margen - u.t0) / 60000));
        this.hi = this.acotar(Math.round((t1 + margen - u.t0) / 60000));
        this.encuadrado = false;
        this.dibujar();
        if (this.$el && this.$el.scrollIntoView) {
          this.$el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },

    /** Marcador "dona": aro coloreado por % sin señal y la duración al centro. */
    iconoParada(p) {
      const pctSin = p.n > 0 ? p.n_sin / p.n : 0;
      const size = Math.min(54, Math.max(30, 26 + Math.log2(Math.max(2, p.dur)) * 4));
      const half = size / 2;
      const grosor = Math.max(4, size * 0.16);
      const r = half - grosor / 2 - 0.5;
      const circ = 2 * Math.PI * r;

      let arcos;
      if (this.modo === "gsm") {
        const col = p.gsm == null ? "#9AA0AE" : gsmColor(p.gsm, this.gsmMax);
        arcos = `<circle cx="${half}" cy="${half}" r="${r}" fill="none" stroke="${col}" stroke-width="${grosor}"/>`;
      } else {
        const lenSin = pctSin * circ;
        arcos =
          `<circle cx="${half}" cy="${half}" r="${r}" fill="none" stroke="#6FA3E0" stroke-width="${grosor}"/>` +
          `<circle cx="${half}" cy="${half}" r="${r}" fill="none" stroke="#E93241" stroke-width="${grosor}" ` +
          `stroke-dasharray="${lenSin} ${circ - lenSin}" transform="rotate(-90 ${half} ${half})"/>`;
      }

      const rInterno = r - grosor / 2 - 0.5;
      const fs = size < 36 ? 9 : 10.5;
      const svg =
        `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">${arcos}` +
        `<circle cx="${half}" cy="${half}" r="${rInterno}" fill="#0A1224"/>` +
        `<text x="${half}" y="${half}" text-anchor="middle" dominant-baseline="central" fill="#F5F4F0" ` +
        `font-family="ui-monospace,monospace" font-weight="500" font-size="${fs}">${fmtDwell(p.dur)}</text></svg>`;

      return L.divIcon({
        className: "",
        html: `<div class="cg-parada">${svg}</div>`,
        iconSize: [size, size],
        iconAnchor: [half, half],
      });
    },

    dibujar() {
      if (!this.mapa) this.crearMapa();
      if (!this.capaPuntos) return;
      this.capaPuntos.clearLayers();
      this.mapa.invalidateSize();

      const u = this.unidad;
      if (!u) {
        this.nota = "";
        return;
      }

      const lo = this.rangoLo;
      const hi = this.rangoHi;
      const pts = u.puntos.filter(
        (p) => p.ts >= lo && p.ts <= hi && (!this.soloConEnergia || p.b === 1),
      );
      const paradas = (u.paradas || []).filter(
        (p) => p.ts1 >= lo && p.ts0 <= hi && (!this.soloConEnergia || p.n_bat > 0),
      );

      let minLa = 90;
      let maxLa = -90;
      let minLo = 180;
      let maxLo = -180;
      let hay = false;
      let nSin = 0;
      const extender = (la, ln) => {
        hay = true;
        if (la < minLa) minLa = la;
        if (la > maxLa) maxLa = la;
        if (ln < minLo) minLo = ln;
        if (ln > maxLo) maxLo = ln;
      };

      pts.forEach((p) => {
        extender(p.la, p.lo);
        if (p.s) nSin++;
        const col = this.modo === "gsm" ? gsmColor(p.g, u.gsmMax) : p.s ? "#E93241" : "#6FA3E0";
        const m = L.circleMarker([p.la, p.lo], {
          renderer: this.renderer,
          radius: 3.2,
          stroke: false,
          fillColor: col,
          fillOpacity: 0.85,
        });
        const texto =
          this.modo === "gsm"
            ? `${p.t} · GSM ${p.g == null ? "—" : p.g}`
            : `${p.t} · ${p.s ? "sin señal" : "con señal"}`;
        m.bindTooltip(texto + (p.b === 0 ? " · sin 12V" : ""), { sticky: true });
        this.capaPuntos.addLayer(m);
      });

      paradas.forEach((p) => {
        extender(p.la, p.lo);
        const m = L.marker([p.la, p.lo], { icon: this.iconoParada(p), zIndexOffset: 1000 });
        m.bindTooltip(
          `Parada ${fmtDwell(p.dur)} · ${pct(p.n > 0 ? (p.n_sin / p.n) * 100 : 0)} sin señal · ` +
            `${nf(p.n)} tramas<br>${p.desde} → ${p.hasta}`,
          { sticky: true },
        );
        this.capaPuntos.addLayer(m);
      });

      this.limites = hay ? L.latLngBounds([[minLa, minLo], [maxLa, maxLo]]) : null;

      const pctSin = pts.length ? (nSin / pts.length) * 100 : 0;
      // Las paradas van agregadas en un marcador aparte, así que puede haber
      // paradas dibujadas sin un solo punto de movimiento.
      if (pts.length || paradas.length) {
        const partes = [`${nf(pts.length)} pts`, `${nf(paradas.length)} parada(s)`];
        if (pts.length) partes.push(`${pct(pctSin)} sin señal`);
        this.nota = `${u.alias} · ${partes.join(" · ")}`;
      } else if (this.soloConEnergia) {
        this.nota = `${u.alias} · ninguna posición del tramo tiene el equipo alimentado por el vehículo. Apagá "Solo con alimentación" para verlas.`;
      } else {
        this.nota = `${u.alias} · sin tramas en este tramo: la unidad no reportó nada. Ampliá el rango con la barra de tiempo.`;
      }

      if (!this.encuadrado && this.limites) {
        this.mapa.fitBounds(this.limites, { padding: [24, 24] });
        this.encuadrado = true;
        setTimeout(() => {
          if (!this.mapa) return;
          this.mapa.invalidateSize();
          if (this.limites) this.mapa.fitBounds(this.limites, { padding: [24, 24] });
        }, 250);
      }
    },
    fmtTB(ms) {
      const d = new Date(ms);
      const p = (n) => String(n).padStart(2, "0");
      return `${p(d.getDate())}/${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`;
    },
  },
};
</script>

<style scoped>
.cg-toolbar {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 12px;
}
.cg-lbl {
  font-family: var(--cg-font-mono);
  font-size: 9.5px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--cg-muted);
}
.cg-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 12px;
  border: 1px solid var(--cg-line);
  border-radius: 20px;
  cursor: pointer;
  font-size: 12.5px;
  color: var(--cg-muted);
  background: transparent;
}
.cg-chip:hover {
  color: var(--cg-text);
}
.cg-chip.activo {
  background: var(--cg-surface-alt);
  color: var(--cg-text);
  border-color: var(--mq-orange);
  box-shadow: inset 0 0 0 1px var(--mq-orange);
  font-weight: 500;
}
.cg-sw {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  display: inline-block;
}
.cg-modos {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  flex-wrap: wrap;
}
.cg-sep {
  width: 1px;
  height: 18px;
  background: var(--cg-line);
  margin: 0 4px;
}
/* Los botones del mapa: el activo tiene que cantar. El "default" de
   light-bootstrap apenas se distingue cuando está apretado. */
.cg-btn {
  padding: 5px 11px;
  font-size: 12px;
  font-family: inherit;
  color: var(--cg-muted);
  background: var(--cg-surface);
  border: 1px solid var(--cg-line);
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
}
.cg-btn:hover {
  color: var(--cg-text);
  border-color: var(--cg-muted);
}
.cg-btn.activo {
  background: var(--mq-orange);
  border-color: var(--mq-orange);
  color: #241400;
  font-weight: 600;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.06);
}
/* :focus-visible y no :focus: si no, el contorno queda pegado después del clic. */
.cg-btn:focus-visible {
  outline: 2px solid rgba(255, 160, 15, 0.45);
  outline-offset: 1px;
}
.cg-mapa {
  height: 540px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid var(--cg-line);
}
.cg-timebar {
  margin-top: 12px;
  background: var(--cg-surface-alt);
  border: 1px solid var(--cg-line);
  border-radius: 6px;
  padding: 12px 14px;
}
.cg-tb-top {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}
.cg-select {
  width: auto;
  display: inline-block;
  height: 30px;
  padding: 2px 8px;
  font-size: 12.5px;
}
.cg-tb-rango {
  margin-left: auto;
  font-family: var(--cg-font-mono);
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  color: var(--cg-text);
}
.cg-slider {
  position: relative;
  height: 24px;
}
.cg-track {
  position: absolute;
  top: 10px;
  left: 0;
  right: 0;
  height: 4px;
  background: var(--cg-line);
  border-radius: 2px;
}
.cg-fill {
  position: absolute;
  height: 100%;
  background: var(--mq-orange);
  border-radius: 2px;
}
.cg-slider input[type="range"] {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 24px;
  margin: 0;
  background: none;
  -webkit-appearance: none;
  appearance: none;
  pointer-events: none;
}
.cg-slider input[type="range"]::-webkit-slider-runnable-track {
  background: none;
  border: none;
}
.cg-slider input[type="range"]::-moz-range-track {
  background: none;
  border: none;
}
.cg-slider input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  pointer-events: auto;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--mq-orange);
  border: 2px solid #fff;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  margin-top: -6px;
}
.cg-slider input[type="range"]::-moz-range-thumb {
  pointer-events: auto;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--mq-orange);
  border: 2px solid #fff;
  cursor: pointer;
}
.cg-leyenda-mapa {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
  font-family: var(--cg-font-mono);
  font-size: 10px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--cg-muted);
  flex-wrap: wrap;
}
.cg-lg {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.cg-lg i {
  width: 11px;
  height: 11px;
  border-radius: 3px;
  display: inline-block;
}
.cg-barra-gsm {
  display: inline-block;
  height: 10px;
  width: 120px;
  border-radius: 5px;
  background: linear-gradient(90deg, #e93241 0%, #ffa00f 50%, #6fc06f 100%);
}
.cg-nota-mapa {
  margin-left: auto;
  text-transform: none;
  letter-spacing: 0;
}
</style>

<style>
/* Sin scope: Leaflet monta el icono fuera del árbol del componente. */
.cg-parada svg {
  filter: drop-shadow(0 2px 5px rgba(0, 0, 0, 0.45));
}
</style>
